<template>
    <div>
        <!-- Desktop/Tablet Layout -->
        <v-card v-if="!isMobile"
                class="mb-4 scrollable-card">
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ $t('itemsGrid') }}</span>
                <div class="d-flex align-center">
                    <v-text-field v-model="searchInput"
                                  :label="t('search')"
                                  hide-details
                                  density="compact"
                                  variant="outlined"
                                  prepend-inner-icon="mdi-magnify"
                                  class="mr-2"
                                  style="max-width: 400px; min-width: 200px;"></v-text-field>

                    <v-btn icon
                           variant="plain"
                           @click="showSummaryReportDialog = true">
                        <v-icon>mdi-download</v-icon>
                    </v-btn>
                </div>
            </v-card-title>

            <v-card-text>
            <v-skeleton-loader
                v-if="!showTable"
                type="table"
                class="elevation-0"
                :loading="!showTable"
                :width="'100%'"
                :height="'400px'"
            />

            <v-data-table-server
                v-model:items-per-page="itemsPerPage"
                :headers="headers"
                :items="items"
                :items-length="totalItems"
                :loading="loading"
                :search="search"
                :sort-by="sort"
                :items-per-page-options="itemsPerPageOptions"
                item-value="name"
                must-sort
                @update:options="loadItems"
            >
            <template v-slot:item="{ item, columns }">
                    <tr>
                        <td class="client-id-cell">
                            <div class="d-flex align-center">
                                <template v-if="hasSubItems(item, 1)">
                                    <v-btn :icon="expandIcon(item)"
                                           @click="toggleExpand(item)"
                                           variant="plain"
                                           class="mr-1"
                                           style="width: 24px; height:24px;"></v-btn>
                                </template>

                                <v-chip label variant="text">
                                    <v-icon :icon="item.categoryIcon" class="mr-2"/>
                                    
                                    <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/${item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process'}-client-ids=${item.clientId}&subcategory-ids=${item.subcategoryId}`)"
                                       href="#"
                                       style="color: inherit;">
                                        {{ item.clientId }}
                                    </a>
                                </v-chip>
                            </div>
                        </td>

                        
                        <td>
                            <div class="d-flex flex-column align-start">
                                <span class="text-start">{{ item.profileTitle }}</span>
                                <span v-if="!hasSubItems(item, 1)" style="color: #888;" class="text-start text-caption">
                                    {{ item.formTemplateTitle !== item.profileTitle ? item.formTemplateTitle : '' }}
                                </span>
                            </div>
                        </td>

                        <td>
                            <template v-if="!hasSubItems(item, 1)">
                                {{ item.subItems[0].assigneeTeamName }}
                            </template>
                        </td>

                        <td>
                            <div class="d-flex align-center">
                                <template v-if="hasAssignees(item)">
                                    <!-- Display the first 5 avatars -->
                                    <v-tooltip v-for="user in getUniqueUsers(item.subItems, 5)"
                                               v-bind:key="id"
                                               :text="user.displayName"
                                               location="bottom">
                                        <template v-slot:activator="{ props }">
                                            <v-avatar v-bind="props" size="28" class="mr-1">
                                                <v-img v-if="user.avatarUrl"
                                                       :src="getAvatarUrl(user.avatarUrl)"
                                                       alt="User Avatar"></v-img>
                                                <span v-else
                                                    class="d-flex justify-center align-center text-caption white--text"
                                                    :style="{
                                                    backgroundColor: getRandomColor(user.id),
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '50%'
                                                  }">
                                                        {{ getInitials(user.displayName || '') }}
                                                    </span>
                                                </v-avatar>
                                            </template>
                                        </v-tooltip>

                                        <!-- If there are more than 5, show a +[number] with a vertical list in tooltip -->
                                        <v-tooltip v-if="getUniqueUsers(item.subItems, 999).length > 5" location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <span v-bind="props" class="mr-1">
                                                    +{{ getUniqueUsers(item.subItems, 999).length - 5 }}
                                                </span>
                                            </template>
                                            <template v-slot:default>

                                                <v-list-item v-for="user in getUniqueUsers(item.subItems, 999).slice(5)"
                                                             :key="user.id"
                                                             class="px-2 py-1">
                                                    <template v-slot:prepend>
                                                        <v-avatar size="22" class="mr-2">
                                                            <v-img v-if="user.avatarUrl"
                                                                   :src="getAvatarUrl(user.avatarUrl)"
                                                                   alt="User Avatar"></v-img>
                                                            <span v-else
                                                                  class="d-flex justify-center align-center text-caption"
                                                                  :style="{
                                                                        backgroundColor: getRandomColor(user.id),
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        borderRadius: '50%'
                                                                    }">
                                                                {{ getInitials(user.displayName || '') }}
                                                            </span>
                                                        </v-avatar>
                                                    </template>
                                                    <v-list-item-title class="text-caption">
                                                        {{ user.displayName || 'Unknown User' }}
                                                    </v-list-item-title>
                                                </v-list-item>

                                            </template>
                                        </v-tooltip>
                                    </template>
                            </div>
                        </td>

                        <td>
                            <div>
                                <v-icon icon="mdi-map" size="small" class="mr-1"></v-icon>
                                <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${item.drawingId}`)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.drawingTitle }}
                                </a>
                            </div>
                            <div>
                                <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                                <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${item.drawingId}#view/location${item.locationId}`)"
                                   href="#"
                                   style="color: inherit;">
                                    {{ item.locationTitle }}
                                </a>
                            </div>
                        </td>

                        <td>
                            <div class="d-flex flex-column">
                                <span v-if="item.createdByTeamName">
                                    <v-icon icon="mdi-account-group" size="small" class="mr-1"></v-icon>{{ item.createdByTeamName }}
                                </span>
                                <span v-if="item.createdByUserName" class="text-caption">
                                    <v-icon icon="mdi-account" size="small" class="mr-1"></v-icon>{{ item.createdByUserName }}
                                </span>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column">
                                <span>{{ formatDate(item.updatedDate) }}</span>
                                <span style="color: #888;" class="text-caption">{{ getDaysSinceText(item.updatedDate) }}</span>
                            </div>
                        </td>
                        <td>
                            <v-chip :style="{
                                backgroundColor: item.latestStatusColor,
                                color: 'white'
                            }"
                                    label>
                                {{ item.latestStatusTitle }}
                            </v-chip>
                        </td>

                    </tr>

                    <tr v-if="isExpanded(item) && item.subItems.length > 1">
                        <td :colspan="columns.length">
                            <v-data-table :headers="subItemHeaders" :items="item.subItems" :items-per-page="-1" item-value="id" hide-default-footer class="elevation-0">
                                <template #[`item.itemTemplateId`]="{ item: subItem }">
                                    {{ subItem.formTemplateTitle }}
                                </template>

                                <template #[`item.assigneeTeamId`]="{ item: subItem }">
                                    {{ subItem.assigneeTeamName || '-' }}
                                </template>

                                <template #[`item.assigneeUserIds`]="{ item: subItem }">
                                    <div class="d-flex align-center">
                                        <template v-if="subItem.assigneeUsers && subItem.assigneeUsers.length > 0">
                                            <v-tooltip v-for="user in subItem.assigneeUsers.slice(0, 5)"
                                                       :key="user.id"
                                                       :text="user.displayName"
                                                       location="bottom">
                                                <template v-slot:activator="{ props }">
                                                    <v-avatar v-bind="props" size="28" class="mr-1">
                                                        <v-img v-if="user.avatarUrl"
                                                               :src="getAvatarUrl(user.avatarUrl)"
                                                               alt="User Avatar"></v-img>
                                                        <span v-else
                                                              class="d-flex justify-center align-center text-caption white--text"
                                                              :style="{
                                                                    backgroundColor: getRandomColor(user.id),
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '50%'
                                                                }">
                                                                {{ getInitials(user.displayName || '') }}
                                                            </span>
                                                        </v-avatar>
                                                    </template>
                                                </v-tooltip>

                                                <span v-if="subItem.assigneeUserIds && subItem.assigneeUserIds.length > 5" class="ml-1">
                                                    +{{ subItem.assigneeUserIds.length - 5 }}
                                                </span>
                                            </template>

                                            <span v-else>-</span>
                                        </div>
                                    </template>

                                    <template #[`item.updatedDate`]="{ item: subItem }">
                                        <div class="d-flex flex-column">
                                            <span>{{ formatDate(subItem.statusCreatedDate) }}</span>
                                            <span style="color: #888;" class="text-caption">{{ getDaysSinceText(subItem.statusCreatedDate) }}</span>
                                        </div>
                                    </template>

                                    <template #[`item.statusId`]="{ item: subItem }">
                                        <v-chip v-if="subItem.statusTitle"
                                                :style="{
                                                    backgroundColor: subItem.statusColor || '#999',
                                                    color: 'white'
                                                }"
                                                label>
                                            {{ subItem.statusTitle }}
                                        </v-chip>
                                        <span v-else>-</span>
                                    </template>
                                </v-data-table>
                            </td>
                        </tr>
                    </template>
                </v-data-table-server>
            </v-card-text>
        </v-card>

        <!-- Mobile Layout -->
        <v-card v-if="isMobile"
                class="mb-4 scrollable-card">
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ $t('itemsGrid') }}</span>
                <div class="d-flex align-center">
                    <v-text-field v-model="searchInput"
                                  :label="t('search')"
                                  hide-details
                                  density="compact"
                                  variant="outlined"
                                  prepend-inner-icon="mdi-magnify"
                                  class="mr-2"
                                  style="min-width: 110px;"></v-text-field>

                    <v-btn v-bind="props" icon
                           variant="plain"
                           @click="showSummaryReportDialog = true">
                        <v-icon>mdi-download</v-icon>
                    </v-btn>
                </div>
            </v-card-title>

            <v-card-text>
                <v-skeleton-loader v-if="!showTable"
                                   type="table"
                                   class="elevation-0"
                                   :loading="!showTable"
                                   :width="'100%'"
                                   :height="'400px'" />
                <!-- Mobile Layout -->
                <v-data-table-server v-if="isMobile && showTable"
                            class="elevation-0 mobile-data-table"
                            :expand-on-click="false"
                            :locale="$i18n.locale"
                            :no-data-text="$t('noData')"
                            :items-per-page-text="$t('itemsPerPage')"
                            v-model:items-per-page="itemsPerPage"
                            :headers="mobileHeaders"
                            :items="items"
                            :items-length="totalItems"
                            :loading="loading"
                            :search="search"
                            :sort-by="sort"
                            :items-per-page-options="itemsPerPageOptions"
                            item-value="name"
                            must-sort
                            @update:options="loadItems"
                            :mobile="true"
                            >                          
                            <template v-slot:item="{ item }">
                        <tr>
                            <td>
                                <v-card class="mb-2" variant="flat">
                                    <v-card-title class="d-flex justify-space-between align-center">
                                        <v-chip label variant="text">
                                            <v-icon v-if="itemTemplatesObj && categoriesObj && item.profileId && itemTemplatesObj[item.profileId] && categoriesObj[itemTemplatesObj[item.profileId].categoryId]" v-bind="getIconClass(categoriesObj[itemTemplatesObj[item.profileId].categoryId].icon)" class="mr-2"></v-icon>
                                            <a v-if="itemTemplatesObj && item.profileId && itemTemplatesObj[item.profileId]"
                                               @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/${item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process'}-client-ids=${item.clientId}&subcategory-ids=${itemTemplatesObj[item.profileId].subcategoryId}`)"
                                               href="#"
                                               style="color: inherit;">
                                                {{ item.clientId }}
                                            </a>
                                            <span v-else>{{ item.clientId }}</span>
                                        </v-chip>
                                        <v-chip v-if="statusTemplatesObj && item.subItems && item.subItems[0] && item.subItems[0].statusId && statusTemplatesObj[item.subItems[0].statusId]"
                                                :style="{
                                                backgroundColor: statusTemplatesObj[item.subItems[0].statusId].color,
                                                color: 'white',
                                                marginTop:'5px'
                                                }"
                                                label>
                                            {{ statusTemplatesObj[item.subItems[0].statusId].title }}
                                        </v-chip>
                                    </v-card-title>

                                    <v-card-text>
                                        <div class="mb-2 text-h6">
                                            {{ item.profileTitle }}
                                        </div>

                                        <div class="mb-2 d-flex justify-space-between" v-if="item.subItems.length === 1">
                                            <strong style="flex-shrink: 0">{{ $t('assignedTo') }}:</strong>
                                            <div class="d-flex flex-column align-end">
                                                <span>{{ item.subItems[0].assigneeTeamName ? item.subItems[0].assigneeTeamName : '-' }}</span>
                                                <div class="d-flex justify-end mt-1">
                                                    <template v-if="item.subItems[0].assigneeUserIds && item.subItems[0].assigneeUserIds.length > 0">
                                                        <v-avatar v-for="userId in item.subItems[0].assigneeUserIds.slice(0, 3)"
                                                                  :key="userId"
                                                                  size="28"
                                                                  class="mr-1">
                                                            <v-img v-if="usersObj[userId] && usersObj[userId].avatarUrl"
                                                                   :src="getAvatarUrl(usersObj[userId].avatarUrl)"
                                                                   alt="User Avatar"></v-img>
                                                            <span v-else
                                                                  class="d-flex justify-center align-center text-caption white--text"
                                                                  :style="{
                                                            backgroundColor: getRandomColor(userId),
                                                            width: '100%',
                                                            height: '100%',
                                                            borderRadius: '50%'
                                                        }">
                                                                {{ getInitials(usersObj[userId] ? usersObj[userId].displayName : '') }}
                                                            </span>
                                                        </v-avatar>
                                                        <span v-if="item.subItems[0].assigneeUserIds.length > 3" class="ml-1">
                                                            +{{ item.subItems[0].assigneeUserIds.length - 3 }}
                                                        </span>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mb-2 d-flex justify-space-between">
                                            <strong style="flex-shrink: 0">{{ $t('location') }}:</strong>
                                            <div class="d-flex flex-column align-end" style="max-width: calc(100% - 80px);">
                                                <div style="width: 100%; display: flex; justify-content: flex-end;">
                                                    <div style="display: flex; align-items: flex-start; text-align: right;">
                                                        <v-icon icon="mdi-map" size="small" style="flex-shrink: 0; margin-right: 4px; margin-top: 2px;"></v-icon>
                                                        <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${item.drawingId}`)"
                                                            href="#"
                                                            style="color: inherit;">
                                                            {{ item.drawingTitle }}
                                                        </a>
                                                    </div>
                                                </div>
                                                <div style="width: 100%; display: flex; justify-content: flex-end; margin-top: 4px;">
                                                    <div style="display: flex; align-items: flex-start; text-align: right;">
                                                        <v-icon icon="mdi-map-marker" size="small" style="flex-shrink: 0; margin-right: 4px; margin-top: 2px;"></v-icon>
                                                        <a @click.prevent="openUrl(`https://${settings.apiUrl.startsWith('api-') ? settings.apiUrl.slice(4) : settings.apiUrl}/${settings.projectCode}/plan-viewer/${item.drawingId}#view/location${item.locationId}`)"
                                                            href="#"
                                                            style="color: inherit;">
                                                            {{ item.locationTitle }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mb-2 d-flex justify-space-between">
                                            <strong style="flex-shrink: 0">{{ $t('createdBy') }}:</strong>
                                            <div class="d-flex flex-column align-end">
                                                <div class="d-flex align-center" v-if="item.createdByTeamName">
                                                    <v-icon icon="mdi-account-group" size="small" class="mr-1"></v-icon>{{ item.createdByTeamName }}
                                                </div>
                                                <div class="d-flex align-center text-caption" v-if="item.createdByUserName">
                                                    <v-icon icon="mdi-account" size="small" class="mr-1"></v-icon>{{ item.createdByUserName }}
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mb-2" style="display: grid; grid-template-columns: auto 1fr auto; gap: 8px; align-items: center;">
                                            <strong style="flex-shrink: 0">{{ $t('updatedDate') }}:</strong>
                                            <span class="d-flex align-center" :style="{
                                                'text-align': item.subItems.length > 1 ? 'center' : 'right',
                                                'justify-content': item.subItems.length > 1 ? 'center' : 'flex-end'
                                            }">
                                                {{ formatDate(item.updatedDate) }}
                                            </span>
                                            <template v-if="item.subItems.length > 1">
                                                <v-btn prepend-icon="mdi-file-document-outline"
                                                       :text="item.subItems.length"
                                                       variant="outlined"
                                                       @click="showSubItemsDialog = true; selectedSubItems = item"></v-btn>
                                            </template>
                                            <template v-else>
                                                <span></span> <!-- Empty column when button is not shown -->
                                            </template>
                                        </div>

                                    </v-card-text>
                                </v-card>
                            </td>
                        </tr>
                    </template>
                </v-data-table-server>
            </v-card-text>
        </v-card>

        <ItemsGridPaginatedReportsDialog
            :showSummaryReportDialog="showSummaryReportDialog"
            :itemsView="itemsView"
            :search="searchQuery"
            @update:showSummaryReportDialog="showSummaryReportDialog = $event"
        />
    </div>
</template>

<script setup>
    import { ref, computed, defineProps, getCurrentInstance, onMounted, onBeforeUnmount, watch } from 'vue';
    import { useStore } from 'vuex';
    import { format, differenceInDays } from 'date-fns';
    import { useI18n } from 'vue-i18n';
    import { useDebounce } from '@/utils/useDebounce';
    import ItemsGridPaginatedReportsDialog from '@/components/dialogs/ItemsGridPaginatedReportsDialog.vue';
    

    const props = defineProps({
        filterContext: {
            type: Object,
            required: true
        },
        // Source view for item rows; the Activity page passes its
        // status-history-scoped view instead of the default filter_context one
        itemsView: {
            type: String,
            default: 'filteredItems'
        }
    });

    const store = useStore();
    const { t } = useI18n();

    // Simple mobile detection
    const windowWidth = ref(window.innerWidth);
    const isMobile = computed(() => windowWidth.value < 960);

    // Update window width on resize
    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
      window.addEventListener('resize', updateWidth);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateWidth);
    });

    const settings = computed(() => store.state.settings);
    const buildMode = computed(() => store.state.buildMode);
    const conn = store.getters.getDBConnection;
    const statusTemplatesObj = computed(() => store.getters.getStatusTemplatesObj);
    const currentUserId = computed(() => store.getters.getUserId);

    // Local users cache loaded from DuckDB
    const usersObj = ref({});

    const loadUsersFromDB = async () => {
        if (Object.keys(usersObj.value).length > 0 && !usersObj.value['undefined']) return; // Already loaded
        try {
            // Load users from key-value structured table
            // Schema: Key (team ID) | Value (JSON array of users)
            const result = await conn.query('SELECT Key, Value FROM users');
            const users = {};

            result.toArray().forEach(row => {
                const rowData = row.toJSON();
                const teamId = rowData.Key || rowData.key;
                let teamUsers = rowData.Value || rowData.value;

                // Parse the JSON string to get array of users
                if (typeof teamUsers === 'string') {
                    try {
                        teamUsers = JSON.parse(teamUsers);
                    } catch (e) {
                        teamUsers = [];
                    }
                }

                // Extract users from this team
                if (Array.isArray(teamUsers)) {
                    teamUsers.forEach(u => {
                        const id = u.Id || u.id;
                        if (id) {
                            // Only add if not already added (avoid duplicates across teams)
                            if (!users[id]) {
                                users[id] = {
                                    displayName: u.DisplayName || u.displayName || '',
                                    avatarUrl: u.AvatarUrl || u.avatarUrl || ''
                                };
                            }
                        }
                    });
                }
            });

            usersObj.value = users;
        } catch (error) {
            console.error('Error loading users from DB:', error);
        }
    };

    // Pagination state
    const loading = ref(false);
    const items = ref([]);
    const totalItems = ref(0);
    const page = ref(1);
    const itemsPerPage = ref(10);
    const itemsPerPageOptions = [
      { title: '10', value: 10 },
      { title: '50', value: 50 },
      { title: '100', value: 100 },
      { title: '150', value: 150 },
      { title: '200', value: 200 }
    ];

    const showTable = ref(false);
    const searchInput = ref('');
    const searchQuery = useDebounce(searchInput, 300);
    const expanded = ref([]);
    const sort = ref([{ key: 'StatusCreatedDate', order: 'desc' }]);
    const selectedSubItems = ref(null)
    const showSubItemsDialog = ref(false)

    const showSummaryReportDialog = ref(false)

    // Function to load data from DuckDB
    const loadItems = async (options = {}) => {
        loading.value = true;
        if (options.page && options.page !== page.value) {
            expanded.value = [];
        }

        // Ensure users are loaded from DB
        await loadUsersFromDB();

        try {
            // Extract current options or use defaults
            const currentPage = options.page || page.value;
            const currentItemsPerPage = options.itemsPerPage || itemsPerPage.value;
            let currentSort = sort.value[0];
            const searchTerm = searchQuery.value;

            if (options.sortBy && options.sortBy.length) {
                currentSort = {
                    key: options.sortBy[0].key,
                    order: options.sortBy[0].order
                };
                sort.value = [currentSort];
            }

            // Call the function to fetch paginated data
            const result = await fetchPaginatedData({
                page: currentPage,
                itemsPerPage: currentItemsPerPage,
                sort: currentSort,
                search: searchTerm
            });

            // Update the component state with the response data
            items.value = result.items;
            totalItems.value = parseInt(result.totalItems, 10);

            page.value = currentPage;
            itemsPerPage.value = currentItemsPerPage;

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            loading.value = false;
        }
    };

    // Function to fetch data from DuckDB with all the required information
    const fetchPaginatedData = async ({ page, itemsPerPage, sort, search }) => {
    try {
        // Build the WHERE clause for search
        const whereClause = getWhereClause(search);
        
        
        // Build the ORDER BY clause with proper validation
        let orderByColumn = 'fi.Id'; // Default sort column (capital I for DuckDB case sensitivity)
        let orderDirection = sort.order || 'desc';
        
        // Track if we need JOINs for sorting
        let needsJoinsForSort = false;

        if (sort.key) {
            if (sort.key.startsWith('subitem_')) {
                orderByColumn = sort.key;
            } else {
                // Map column names to their proper database fields
                const columnMapping = {
                    'profileId': 'it.title',
                    'locationId': 'l.title',
                    'StatusId': 'st.title',
                    'statusId': 'st.title',
                    'AssigneeTeamId': 't.name',
                    'assigneeTeamId': 't.name',
                    'AssigneeUserIds': 'json_array_length(fi.assigneeUserIds)',
                    'assigneeUserIds': 'json_array_length(fi.assigneeUserIds)',
                    'createdByTeamId': 'ct.name',
                    'clientId': 'fi.clientId',
                    'StatusCreatedDate': 'fi.statusCreatedDate',
                    'statusCreatedDate': 'fi.statusCreatedDate',
                    'updatedDate': 'fi.statusCreatedDate'
                };

                orderByColumn = columnMapping[sort.key] || `fi.${sort.key.toLowerCase()}`;

                // Check if the sort column requires JOINs (references other tables)
                needsJoinsForSort = orderByColumn.startsWith('it.') ||
                                   orderByColumn.startsWith('l.') ||
                                   orderByColumn.startsWith('st.') ||
                                   orderByColumn.startsWith('t.') ||
                                   orderByColumn.startsWith('ct.');
            }
        }

        // Text columns get case-insensitive ordering; numeric/date columns must keep their native sort
        const caseInsensitiveColumns = new Set(['it.title', 'l.title', 'st.title', 't.name', 'ct.name']);
        const orderByExpression = caseInsensitiveColumns.has(orderByColumn) ? `lower(${orderByColumn})` : orderByColumn;

        let orderByClause = `ORDER BY ${orderByExpression} ${orderDirection.toUpperCase()}`;

        // clientId values like "Issue-100" sort by prefix first, then numeric suffix as an integer,
        // so "Issue-2" comes before "Issue-10" (mirrors compareClientIds on main)
        if (orderByColumn === 'fi.clientId') {
            const direction = orderDirection.toUpperCase();
            const nullsPosition = direction === 'ASC' ? 'NULLS FIRST' : 'NULLS LAST';
            orderByClause = `ORDER BY ` +
                `lower(CASE WHEN regexp_matches(fi.clientId, '-[0-9]+$') THEN regexp_replace(fi.clientId, '-[0-9]+$', '') ELSE fi.clientId END) ${direction}, ` +
                `TRY_CAST(nullif(regexp_extract(fi.clientId, '-([0-9]+)$', 1), '') AS BIGINT) ${direction} ${nullsPosition}`;
        }

        // JOINs needed when searching OR when sort column requires them
        const needsJoins = whereClause.length > 0 || needsJoinsForSort;

        const joinsClause = `
            LEFT JOIN item_templates it ON fi.profileId = it.id
            LEFT JOIN categories c ON it.categoryId = c.id
            LEFT JOIN form_templates ft ON fi.SubItemTemplateId = ft.id
            LEFT JOIN status_templates st ON fi.StatusId = st.id
            LEFT JOIN teams t ON fi.AssigneeTeamId = t.id
            LEFT JOIN locations l ON fi.locationId = l.id
            LEFT JOIN drawings ld ON l.drawingId = ld.id
            LEFT JOIN teams ct ON fi.createdByTeamId = ct.id
        `;
        
        // Build the LIMIT and OFFSET clauses
        const limitClause = `LIMIT ${itemsPerPage}`;
        const offsetClause = `OFFSET ${(page - 1) * itemsPerPage}`;
        
        // Count total items
        const countQuery = `
            SELECT COUNT(DISTINCT fi.id) as total
            FROM ${props.itemsView} fi
            ${needsJoins ? joinsClause : ''}
            ${whereClause}
        `;
        
        const countResult = await conn.query(countQuery);
        let myTotalItems = 0;
        if (countResult.numRows > 0) {
            myTotalItems = Number(countResult.toArray()[0].toJSON().total);
        }
        
        //since we have subItems first calculate for the pageSize how many items to return
        const itemsIdsQuery = `
            SELECT DISTINCT
                fi.id
            FROM ${props.itemsView} fi
            ${needsJoins ? joinsClause : ''}
            ${whereClause}
            ${orderByClause}
            ${limitClause}
            ${offsetClause}
        `;

        console.log('🔍 [ItemsGrid] Executing itemsIdsQuery:', itemsIdsQuery);
        const idsResult = await conn.query(itemsIdsQuery);
        const itemIds = idsResult.toArray().map(row => row.toJSON().id);
        console.log('🔍 [ItemsGrid] Got itemIds:', itemIds);
        console.log('🔍 [ItemsGrid] Total items count:', myTotalItems);

        if (itemIds.length === 0) {
            console.log('❌ [ItemsGrid] No items found!');
            return {
                items: [],
                totalItems: myTotalItems
            };
        }

        // Get the main items with all necessary joined data
        const itemsQuery = `
            SELECT
                fi.*,
                it.title             AS profileTitle,
                it.categoryId,
                it.subcategoryId,
                c.icon               AS categoryIcon,
                ft.title             AS formTemplateTitle,
                ft.sort              AS formTemplateSort,
                st.title             AS statusTitle,
                st.color             AS statusColor,
                t.name               AS assigneeTeamName,
                l.title              AS locationTitle,
                l.drawingId          AS drawingId,
                ld.title             AS drawingTitle,
                ct.name              AS createdByTeamName
            FROM ${props.itemsView} fi
            LEFT JOIN item_templates it ON fi.profileId       = it.id
            LEFT JOIN categories     c  ON it.categoryId      = c.id
            LEFT JOIN form_templates ft ON fi.subItemTemplateId = ft.id
            LEFT JOIN status_templates st ON fi.statusId      = st.id
            LEFT JOIN teams          t  ON fi.assigneeTeamId  = t.id
            LEFT JOIN locations      l  ON fi.locationId      = l.id
            LEFT JOIN drawings       ld ON l.drawingId        = ld.id
            LEFT JOIN teams          ct ON fi.createdByTeamId = ct.id
            WHERE fi.id IN (${itemIds.map(id => `'${id}'`).join(',')})
            ${orderByClause};
        `;

        console.log('🔍 [ItemsGrid] Executing itemsQuery:', itemsQuery);
        const itemsResult = await conn.query(itemsQuery);
        console.log('🔍 [ItemsGrid] Got', itemsResult.numRows, 'rows from itemsQuery');

        let groupedItems = {};

        // First pass - group by parent item ID (assuming profileId is what makes a parent item unique)
        itemsResult.toArray().forEach(row => {
            const rawData = row.toJSON();
            console.log('🔍 [ItemsGrid] Processing row with id:', rawData.id, 'Id:', rawData.Id);
            const parentId = rawData.id || rawData.Id; // Handle both lowercase and uppercase Id

            if (!groupedItems[parentId]) {
                // Create parent item the first time we see this parent ID
                groupedItems[parentId] = {
                    id: rawData.id || rawData.Id,
                    clientId: rawData.clientId || rawData.ClientId,
                    profileId: rawData.profileId || rawData.ProfileId,
                    type: rawData.type || rawData.Type,
                    profileTitle: rawData.profileTitle,
                    categoryId: rawData.categoryId,
                    subcategoryId: rawData.subcategoryId,
                    categoryIcon: getIconClass(rawData.categoryIcon),
                    formTemplateTitle: rawData.formTemplateTitle || '',
                    locationId: rawData.locationId || rawData.LocationId,
                    locationTitle: rawData.locationTitle || rawData.LocationTitle,
                    drawingId: rawData.drawingId || rawData.DrawingId,
                    drawingTitle: rawData.drawingTitle || rawData.DrawingTitle,
                    createdByTeamName: rawData.createdByTeamName,
                    createdByUserName: usersObj.value[rawData.createdByUserId || rawData.CreatedByUserId]?.displayName || '',
                    // Initialize the subitems array
                    subItems: []
                };
            }
            
            // Parse assigneeUserIds from the raw data (stored as JSON string in DB)
            let assigneeUserIds = [];
            const rawAssigneeUserIds = rawData.assigneeUserIds || rawData.AssigneeUserIds;
            if (rawAssigneeUserIds) {
                if (typeof rawAssigneeUserIds === 'string') {
                    try {
                        const parsed = JSON.parse(rawAssigneeUserIds);
                        assigneeUserIds = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
                    } catch (e) {
                        // If not valid JSON, try comma-separated
                        assigneeUserIds = rawAssigneeUserIds.split(',').map(id => id.trim()).filter(Boolean);
                    }
                } else if (Array.isArray(rawAssigneeUserIds)) {
                    assigneeUserIds = rawAssigneeUserIds;
                }
            }

            // Convert assigneeUserIds to user objects
            const assigneeUsers = assigneeUserIds
                .filter(id => usersObj.value && usersObj.value[id])
                .map(id => ({
                    id: id,
                    displayName: usersObj.value[id]?.displayName || '',
                    avatarUrl: usersObj.value[id]?.avatarUrl || ''
                }));

            // Add this row as a subitem
            // Handle both PascalCase and camelCase column names from DuckDB
            const subitem = {
                formTemplateTitle: rawData.formTemplateTitle,
                formTemplateSort: rawData.formTemplateSort ?? Number.MAX_SAFE_INTEGER,
                statusId: rawData.statusId || rawData.StatusId,
                statusTitle: rawData.statusTitle,
                statusColor: rawData.statusColor,
                statusCreatedDate: rawData.statusCreatedDate || rawData.StatusCreatedDate,
                assigneeTeamName: rawData.assigneeTeamName || '',
                assigneeUserIds: assigneeUserIds,
                assigneeUsers: assigneeUsers,
                locationTitle: rawData.locationTitle || '',
                drawingId: rawData.drawingId || '',
                drawingTitle: rawData.drawingTitle || ''
            };
            
            // Add the subitem to the parent's subitems array
            groupedItems[parentId].subItems.push(subitem);
        });

        // Convert the grouped items object to an array
        let items = Object.values(groupedItems);
        console.log('🔍 [ItemsGrid] Grouped items count:', items.length);
        console.log('🔍 [ItemsGrid] Sample grouped item:', items[0]);

        // Sort subItems: primary = statusCreatedDate desc, secondary = formTemplateSort asc
        items.forEach(item => {
            if (item.subItems && item.subItems.length > 1) {
                item.subItems.sort((a, b) => {
                    const aDate = a.statusCreatedDate ? new Date(a.statusCreatedDate).getTime() : 0;
                    const bDate = b.statusCreatedDate ? new Date(b.statusCreatedDate).getTime() : 0;
                    if (aDate !== bDate) return bDate - aDate;
                    const aSort = Number(a.formTemplateSort ?? Number.MAX_SAFE_INTEGER);
                    const bSort = Number(b.formTemplateSort ?? Number.MAX_SAFE_INTEGER);
                    return aSort - bSort;
                });
            }
        });

        // Add the new properties to each item by finding the latest statusCreatedDate
        items = items.map(item => {
            // Find the subitem with the latest statusCreatedDate
            let latestSubitem = null;
            
            if (item.subItems && item.subItems.length > 0) {
                latestSubitem = item.subItems.reduce((latest, current) => {
                    // If statusCreatedDate is missing, use a very old date
                    const latestDate = latest.statusCreatedDate ? new Date(latest.statusCreatedDate) : new Date(0);
                    const currentDate = current.statusCreatedDate ? new Date(current.statusCreatedDate) : new Date(0);
                    
                    return currentDate > latestDate ? current : latest;
                }, item.subItems[0]);
            }
            
            // Add the new properties
            return {
                ...item,
                latestStatusTitle: latestSubitem ? latestSubitem.statusTitle : '',
                latestStatusColor: latestSubitem ? latestSubitem.statusColor : '',
                updatedDate: latestSubitem ? latestSubitem.statusCreatedDate : null
            };
        });

        return {
            items,
            totalItems: myTotalItems
        };
        } catch (error) {
            console.error('Error fetching data from DuckDB:', error);
            return {
                items: [],
                totalItems: 0
            };
        }
    };

    const getWhereClause = (search) => {
        let whereClause = '';
        if (search && search.trim()) {
            const searchTerm = search.trim().toLowerCase();
            whereClause = `
                WHERE (
                    LOWER(fi.clientId) LIKE '%${searchTerm}%'
                    OR LOWER(it.title) LIKE '%${searchTerm}%'
                    OR LOWER(t.name) LIKE '%${searchTerm}%'
                    OR LOWER(l.title) LIKE '%${searchTerm}%'
                    OR LOWER(ld.title) LIKE '%${searchTerm}%'
                    OR LOWER(st.title) LIKE '%${searchTerm}%'
                    OR LOWER(ct.name) LIKE '%${searchTerm}%'
                    OR strftime(TRY_CAST(fi.statusCreatedDate AS TIMESTAMP), '%Y-%m-%d') LIKE '%${searchTerm}%'
                )
            `;
        }
        return whereClause;
    }

    // Function to check if an item has multiple sub-items default is 1
    const hasSubItems = (item, minCount = 1) => {
        return item.subItems.length > minCount;
    };

    const hasAssignees = (item) => {
        return item.subItems.some(subItem => subItem.assigneeUserIds && subItem.assigneeUserIds.length > 0);
    };

    const getUniqueAssigneeUserIds = (item) => {
        const userIds = item.subItems.flatMap(subItem => subItem.assigneeUserIds || []);
        return [...new Set(userIds)];
    };

    const getUniqueUsers = (subItems, count) => {
        // Get unique user IDs from all subItems
        const allUserIds = subItems
            .flatMap(item => item.assigneeUserIds || [])
            .filter(Boolean);
        const uniqueUserIds = [...new Set(allUserIds)];

        // Convert IDs to user objects using usersObj
        const users = uniqueUserIds
            .filter(id => usersObj.value && usersObj.value[id])
            .map(id => ({
                id: id,
                displayName: usersObj.value[id]?.displayName || '',
                avatarUrl: usersObj.value[id]?.avatarUrl || ''
            }));

        // Sort by name, then put current user first
        const userId = currentUserId.value?.toString();
        users.sort((a, b) => {
            // Current user always first
            if (a.id === userId) return -1;
            if (b.id === userId) return 1;
            // Then sort alphabetically by displayName
            return (a.displayName || '').localeCompare(b.displayName || '');
        });

        return users.slice(0, count);
    }

    watch(searchQuery, () => {
        loadItems({
            page: 1 // Reset to first page when search changes
        });
    });

    watch(
        () => props.filterContext.filtersLastUpdated,
        () => {
            //grid should be last to run so put it at the end of the queue
            setTimeout(function() {
                loadItems({ page: 1 })
            },100);         
        },
        { deep: true }
    );

    const headers = computed(() => [
        { title: t('id'), key: 'clientId', align: 'start', sortable: true },
        { title: t('item'), key: 'profileId', align: 'start', sortable: true },
        { title: t('assignedTo'), key: 'AssigneeTeamId', sortable: true },
        { title: "", key: "AssigneeUserIds", sortable: true },
        { title: t('location'), key: 'locationId', align: 'start', sortable: true },
        { title: t('createdBy'), key: 'createdByTeamId', align: 'start', sortable: true },
        { title: t('updatedDate'), key: 'StatusCreatedDate', align: 'start', sortable: true },
        { title: t('status'), key: 'StatusId', align: 'start', sortable: true },
    ]);

    const subItemHeaders = [
        { title: t('subItem'), key: 'itemTemplateId', align: 'start', sortable: true },
        { title: t('assignedTo'), key: 'assigneeTeamId', align: 'start', sortable: true },
        { title: '', key: 'assigneeUserIds', align: 'start', sortable: false },
        { title: t('updatedDate'), key: 'updatedDate', align: 'start', sortable: true },
        { title: t('status'), key: 'statusId', align: 'start', sortable: true }
    ];

    const mobileHeaders = [
        { title: 'Items', key: 'items', sortable: false }
    ]


    function getLatestSubItem(subItems) {
        return subItems.reduce((latest, current) => {
            const latestDate = new Date(latest.statusCreatedDate);
            const currentDate = new Date(current.statusCreatedDate);
            const timeDiff = Math.abs(currentDate - latestDate);
            if (timeDiff < 3000) {
                return latest;
            }
            return currentDate > latestDate ? current : latest;
        });
    }

    const { proxy } = getCurrentInstance();
    const openUrl = (url) => {
        if (buildMode.value === 'production') {
            proxy.$launchUrl(url);
        } else {
            window.open(url, '_blank');
        }
    };

    const toggleExpand = (item) => {
        const index = expanded.value.indexOf(item.id);
        if (index === -1) {
            expanded.value.push(item.id);
        } else {
            expanded.value.splice(index, 1);
        }
    };

    const isExpanded = (item) => {
        return expanded.value.includes(item.id);
    };

    const expandIcon = computed(() => (item) => {
        return isExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right';
    });

    const getRandomColor = (id) => {
        const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
        const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
        return colors[index];
    };

    const getInitials = (name) => {
        if (!name) return '';
        const words = name.split(' ');
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        } else if (words[0]) {
            return words[0].substring(0, 2).toUpperCase();
        }
        return '';
    };

    const getAvatarUrl = (avatarUrl) => {
        if (!avatarUrl) {
            return '';
        }
        // If the URL already includes a protocol, return as-is
        if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
            return avatarUrl;
        } else {
            // Otherwise, prepend https://
            return 'https://' + avatarUrl;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return format(date, 'yyyy-MM-dd');
    };

    const getDaysSince = (dateString) => {
        if (!dateString) return 0;
        try {
            const statusDate = new Date(dateString);
            if (isNaN(statusDate.getTime())) return 0;
            const today = new Date();
            return differenceInDays(today, statusDate);
        } catch {
            return 0;
        }
    };

    const getDaysSinceText = (dateString) => {
        const daysSince = getDaysSince(dateString);
        if (daysSince === 0) return t('today');
        if (daysSince === 1) return t('dayAgo', { daysSince });
        if (daysSince > 1) return t('daysAgo', { daysSince });
        return '';
    };

    const getIconClass = (icon) => {
        if (!icon) return 'mdi-help-circle';
        
        if (icon.startsWith('mdi-')) {
            return icon; // Already in correct format for MDI
        } else if (icon.startsWith('fas fa-')) {
            // For Font Awesome 6 in Vuetify 3, format is:
            return `fa:${icon}`;
        } else if (icon.startsWith('fab fa-')) {
            return `fa:${icon}`;
        } else if (icon.startsWith('fa-')) {
            // If just fa- prefix is used, assume it's a solid icon
            return `fa:fas ${icon}`;
        } else {
            // For material icons
            return `md:${icon}`;
        }
    }

    onMounted(async () => {
        showTable.value = true;
        // Load initial data when component mounts
        await loadItems({ page: 1 });
    });

</script>

<style>
    .v-data-table .v-icon {
        opacity: 0.6;
    }

    .mobile-data-table > .v-table__wrapper > table > tbody > tr > td {
        padding: 0 !important;
    }

    .mobile-data-table .v-card-title,
    .mobile-data-table .v-card-text {
        padding: 5px 0 !important;
    }

    .v-data-table-headers--mobile {
        display: none !important;
    }

    .export-btn {
        min-width: 75px;
        margin: 0 2px;
    }
</style>